/**
 * Token-safe client analytics for the modern OAuth2 flow.
 *
 * The marker intentionally contains only the selected flow, completion mode,
 * and an approval timestamp for its TTL. It bridges redirect-mode completion
 * across the Emerge -> BFF navigation without ever persisting an interaction,
 * resume, authorization, or access-token value in browser-readable storage.
 */

export const MODERN_AUTH_FLOWS = ['login', 'signup', 'magic_link'] as const;
export type ModernAuthFlow = (typeof MODERN_AUTH_FLOWS)[number];

export const MODERN_AUTH_COMPLETION_MODES = ['redirect', 'embedded'] as const;
export type ModernAuthCompletionMode = (typeof MODERN_AUTH_COMPLETION_MODES)[number];

export interface ModernAuthAnalyticsContext {
  flow: ModernAuthFlow;
  completionMode: ModernAuthCompletionMode;
}

export interface ModernAuthApproval extends ModernAuthAnalyticsContext {
  approvedAt: number;
}

export const MODERN_AUTH_EVENTS = {
  loginBegin: 'Login Begin',
  loginComplete: 'Login Complete',
  signupComplete: 'Signup Complete',
  magicLinkRequested: 'Magic Link Requested',
  magicLinkApproved: 'Magic Link Approved',
  interactionExpired: 'Auth Interaction Expired',
  interactionRestarted: 'Auth Interaction Restarted',
} as const;

const APPROVAL_STORAGE_KEY = 'pz_modern_auth_approval';
const APPROVAL_TTL_MS = 10 * 60 * 1000;

function isOneOf<T extends string>(value: unknown, values: readonly T[]): value is T {
  return typeof value === 'string' && values.includes(value as T);
}

function sessionStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Coarse dimensions shared with Emerge's `Penzu Next BFF` client telemetry. */
export function modernAuthEventProperties(
  context: ModernAuthAnalyticsContext,
  stateName: string = 'index.new',
): Record<string, string> {
  return {
    state_name: stateName,
    auth_protocol: 'oauth2',
    auth_surface: 'penzu_next',
    oauth_client: 'Penzu Next BFF',
    auth_flow: context.flow,
    completion_mode: context.completionMode,
  };
}

export function modernAuthCompletionEvent(flow: ModernAuthFlow): string {
  return flow === 'signup'
    ? MODERN_AUTH_EVENTS.signupComplete
    : MODERN_AUTH_EVENTS.loginComplete;
}

export function rememberModernAuthApproval(context: ModernAuthAnalyticsContext): void {
  const storage = sessionStorageOrNull();
  if (!storage) return;

  const approval: ModernAuthApproval = { ...context, approvedAt: Date.now() };
  try {
    storage.setItem(APPROVAL_STORAGE_KEY, JSON.stringify(approval));
  } catch {
    // Analytics must never interfere with the authentication completion path.
  }
}

export function currentModernAuthApproval(): ModernAuthApproval | null {
  const storage = sessionStorageOrNull();
  if (!storage) return null;

  let raw: string | null;
  try {
    raw = storage.getItem(APPROVAL_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const approval = JSON.parse(raw) as Partial<ModernAuthApproval>;
    const valid =
      isOneOf(approval.flow, MODERN_AUTH_FLOWS) &&
      isOneOf(approval.completionMode, MODERN_AUTH_COMPLETION_MODES) &&
      typeof approval.approvedAt === 'number' &&
      Date.now() - approval.approvedAt >= 0 &&
      Date.now() - approval.approvedAt <= APPROVAL_TTL_MS;
    if (valid) return approval as ModernAuthApproval;
  } catch {
    // Treat malformed analytics state as absent.
  }

  clearModernAuthApproval();
  return null;
}

export function clearModernAuthApproval(): void {
  const storage = sessionStorageOrNull();
  if (!storage) return;

  try {
    storage.removeItem(APPROVAL_STORAGE_KEY);
  } catch {
    // Analytics cleanup is best-effort.
  }
}
